import { SubscriptionsApi, PlansApi } from 'Api';
import { uniqueId } from 'lodash';
import moment from 'moment';
import { createContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as alertActions from '../../Redux/Actions/alertActions';
import { amountDue } from 'Components/Setup/Billing/utils/Utils';
import { API as api } from 'Backend/backend-access';
import { planMap } from 'Components/Setup/Billing/utils/Utils';
import { getToken } from '../../Lib/Utils';

export const NotificationsContext = createContext();

export const NotificationsContextProvider = (props) => {
    const dispatch = useDispatch();
    const { cntNewAlert, nextToken } = useSelector((state) => state.alert);
    const { company } = useSelector((state) => state.user.data);
    const [loading, setLoading] = useState(true);
    const [subscription, setSubscription] = useState(null);
    const [plan, setPlan] = useState(null);
    const [alertsNextToken, setAlertsNextToken] = useState(null);
    const [newAlerts, setNewAlerts] = useState(0);
    const [alertsOpen, setAlertsOpen] = useState(false);
    const [alerts, setAlerts] = useState([]);
    const [updates, setUpdates] = useState([]);
    const [actions, setActions] = useState([]);
    const [guides, setGuides] = useState([]);
    const [companyUsers, setCompanyUsers] = useState([]);

    const validationPlanIds = ['85209bdc-d8ed-4359-913f-3dce5bb9e22e', '85209bdc-d8ed-4359-913f-3dce5bb9e22d'];

    useEffect(() => {
        if (!loading) return;
        getInitialData();
    }, []);

    useEffect(() => {
        if (!company.Id) return;
        getInitialData();
    }, [company.Id]);

    useEffect(() => {
        if (!alertsOpen) {
            setTimeout(() => {
                dispatch(alertActions.markSeen({ Timestamp: -1 }));
            }, 200);
        }
    }, [alertsOpen]);

    const loadPlan = async (sub) => {
        const currentPlan = await PlansApi.find('plan', sub?.planId).then((res) => res.data);
        setPlan(currentPlan);
    };

    useEffect(() => {
        checkSubscription();
        if (!plan && subscription) loadPlan(subscription);
    }, [subscription, companyUsers]);

    const getAlerts = async () => {
        return dispatch(alertActions.getList(null)).catch((err) =>
            console.log('Failed to get notifications alerts. ', err)
        );
    };

    const getInitialData = async () => {
        clearNotifications();

        const accessToken = await getToken();
        if (accessToken) {
            try {
                const alertsData = await getAlerts().catch((err) => console.log('Failed to fetch alerts: ', err));
                setNewAlerts(cntNewAlert);
                setAlertsNextToken(nextToken);
                setAlerts(alertsData?.items || []);
            } catch (err) {
                console.log('error fetching alerts', err);
            }

            try {
                const result = await SubscriptionsApi.findAll();
                const sub = validateSubscription(result?.data[0] || {});
                loadCompanyUsers();
                setSubscription(sub);
            } catch (err) {
                console.log('error fetching subscription', err);
            }
        }

        setLoading(false);
    };

    const loadCompanyUsers = async () => {
        const paidUsers = await api.getNonTenantUsers().then((res) => res.data?.users);
        // INSPECT: All users here have Paid: true, yet further down when we use this array we check for !Paid and Paid in userBreakdown (in ptrn-fe/src/Components/Setup/Billing/utils/Utils.tsx). Why??
        setCompanyUsers(paidUsers.map((user) => ({ ...user, Paid: true })));
    };

    const addAlert = (alert) => {
        let currentAlerts = alerts;
        if (!alerts.some((a) => a.type === alert.type && a.message === alert.message)) {
            currentAlerts = [alert, ...currentAlerts];
        }
        setAlerts(currentAlerts);
    };

    const addUpdate = (update) => {
        let currentUpdates = updates;
        if (!updates.some((u) => u.type === update.type && u.message === update.message)) {
            currentUpdates = [update, ...currentUpdates];
        }
        setUpdates(currentUpdates);
    };

    const addAction = (action) => {
        let currentActions = actions;
        if (!actions.some((a) => a.type === action.type && a.message === action.message)) {
            currentActions = [action, ...currentActions];
        }
        setActions(currentActions);
    };

    const removeAlert = (alert) => {
        setAlerts(alerts.filter((n) => n.id !== alert.id));
    };

    const removeUpdate = (update) => {
        setUpdates(updates.filter((u) => u.id !== update.id));
    };

    const removeAction = (action) => {
        setActions(actions.filter((a) => a.id === action.id));
    };

    const markAllAsRead = async () => {
        await dispatch(alertActions.markSeen({ Timestamp: 0 }));
        const updatedAlerts = alerts.map((a) => {
            a = {
                ...a,
                isRead: true,
            };
            return a;
        });
        setAlerts(updatedAlerts);
    };

    const loadMoreAlerts = async () => {
        setLoading(true);
        await dispatch(alertActions.getList(alertsNextToken));
        setLoading(false);
    };

    const alertClick = async (notification) => {
        const updatedAlerts = alerts.map((a) => {
            if (a.Timestamp === notification.Timestamp) {
                a = {
                    ...a,
                    isRead: true,
                };
            }
            return a;
        });
        setAlerts(updatedAlerts);
        await dispatch(alertActions.markSeen({ Timestamp: notification.Timestamp }));
        setAlertsOpen(false);
    };

    const clearNotifications = () => {
        setAlerts([]);
        setUpdates([]);
        setActions([]);
        setGuides([]);
    };

    const refreshNotifications = () => {
        getInitialData();
    };

    const validateSubscription = (result) => {
        if (!result) {
            return {
                ...result,
                errors: {
                    valid: false,
                    reason: 'Failed to find account subscription. Please contact support.',
                    redirect: '/help',
                },
            };
        }
        if (company?.BookkeepingOrAccountingPractice && !result.cardId) {
            return {
                ...result,
                errors: {
                    valid: false,
                    reason: "Our accountant and bookkeeping clients usually pay Paytron's monthly subscription cost on behalf of their clients in order to get volume discounts. Please note this does not include transaction fees, which will always be charged to the client account. We recommend that you set up a payment method for your practice below, then opt to link the subscription to your practice when onboarding a new client.",
                    subMessage: true,
                },
            };
        }

        if (!result.id) {
            return {
                ...result,
                errors: {
                    valid: false,
                    reason: 'To continue using Paytron, please select what plan is best for your business and re-enter your credit card details.',
                    redirect: '/setup/billing',
                    action: {
                        text: 'Go to billing',
                        url: '/setup/billing',
                    },
                    subMessage: true,
                },
            };
        }

        if (result.status === 'active' && !result.cardId && validationPlanIds.includes(result.planId)) {
            return {
                ...result,
                errors: {
                    valid: false,
                    reason: 'Subscription does not have a valid payment method. Please add a payment method to continue using Paytron.',
                    subMessage: true,
                },
            };
        }

        if (result.status === 'cancelled') {
            return {
                ...result,
                errors: {
                    valid: false,
                    reason: 'Subscription has been cancelled. Please re-subscribe to continue using Paytron.',
                },
            };
        }

        if (result.status === 'suspended') {
            return {
                ...result,
                errors: {
                    valid: false,
                    reason: 'Subscription has been suspended. Please update billing details to continue using Paytron or contact support.',
                },
            };
        }

        if (result.status === 'trial_expired') {
            return {
                ...result,
                errors: {
                    valid: false,
                    reason: 'Your trial period has expired. Please choose a plan to continue using Paytron.',
                },
            };
        }
        return result;
    };

    const checkSubscription = () => {
        if (
            subscription?.planId === planMap('Business Account') &&
            amountDue(subscription, undefined, plan, companyUsers) > 0 &&
            !subscription.cardId &&
            !subscription.customerCancelDate
        ) {
            addUpdate({
                id: uniqueId('update_'),
                type: 'danger',
                message: `You've added paid users and/or features to a free plan. Please add a payment method to complete your upgrade.`,
                action: {
                    text: 'Go to billing',
                    url: '/setup/billing',
                },
            });
            return;
        }

        if (!subscription || subscription?.planId === planMap('Business Account')) return;

        if (subscription.errors) {
            addUpdate({
                id: uniqueId('update_'),
                type: 'danger',
                message: subscription.errors.reason,
                forceRedirect: subscription.errors.redirect || '',
                ...(subscription.errors.action && { action: subscription.errors.action }),
                ...(subscription.errors?.subMessage && { subMessage: subscription.errors?.subMessage }),
            });
            return;
        }

        if (!subscription.id) {
            addUpdate({
                id: uniqueId('update_'),
                type: 'danger',
                message: `To continue using Paytron, please select what plan is best for your business and re-enter your credit card details.`,
                action: {
                    text: 'Go to billing',
                    url: '/setup/billing',
                },
            });
            return;
        }

        if (
            subscription.status === 'in_trial' &&
            !subscription.cardId &&
            !subscription.customerCancelDate &&
            moment(subscription.trial.endDate).diff(moment(), 'days') <= 7
        ) {
            addUpdate({
                id: uniqueId('update_'),
                type: 'danger',
                message: `Your trial ends in less than ${moment(subscription.trial.endDate).toNow(
                    true
                )}. Update your payment method to keep using Paytron after ${moment(subscription.trial.endDate).format(
                    'MMMM Do'
                )}`,
                action: {
                    text: 'Go to billing',
                    url: '/setup/billing',
                },
            });

            addAction({
                id: uniqueId('action_'),
                type: 'danger',
                message: `Your trial ends on ${moment(subscription.trial.endDate).format('MMMM Do')}`,
                action: {
                    text: 'Go to billing',
                    url: '/setup/billing',
                },
            });
        } else if (
            subscription.status === 'in_trial' &&
            !subscription.cardId &&
            !subscription.customerCancelDate &&
            moment(subscription.trial.endDate).diff(moment(), 'months', true) >= 0.2
        ) {
            addUpdate({
                id: uniqueId('update_'),
                type: 'info',
                message: `Your trial ends on ${moment(subscription.trial.endDate).format(
                    'MMMM Do'
                )}. Update your payment method to keep using Paytron`,
                action: {
                    text: 'Keep my account',
                    url: '/setup/billing',
                },
            });

            addAction({
                id: uniqueId('action_'),
                type: 'info',
                message: `Your trial ends on ${moment(subscription.trial.endDate).format('MMMM Do')}`,
                action: {
                    text: 'Keep my account',
                    url: '/setup/billing',
                },
            });
        }

        if (subscription.customerCancelDate && moment(subscription.cutoffDate).diff(moment(), 'days') <= 7) {
            addUpdate({
                id: uniqueId('update_'),
                type: 'danger',
                message: `Your Paytron subscription ends in ${moment(subscription.cutoffDate).toNow(
                    true
                )}. Your account will freeze, please withdraw your funds beforehand.`,
                action: {
                    text: 'Resubscribe',
                    url: '/setup/billing',
                },
            });

            addAction({
                id: uniqueId('action_'),
                type: 'danger',
                message: `Your subscription ends soon`,
                action: {
                    text: 'Resubscribe',
                    url: '/setup/billing',
                },
            });
        }
    };

    return (
        <NotificationsContext.Provider
            value={{
                loading,
                subscription,
                plan,
                company,
                alerts,
                updates,
                actions,
                guides,
                alertsNextToken,
                newAlerts,
                alertsOpen,
                getInitialData,
                addAlert,
                addUpdate,
                addAction,
                removeAlert,
                removeUpdate,
                removeAction,
                refreshNotifications,
                markAllAsRead,
                loadMoreAlerts,
                alertClick,
                setAlertsOpen,
            }}
        >
            {props.children}
        </NotificationsContext.Provider>
    );
};
